<template>
  <div class="ne-table-wrap">
    <div class="ne-header">
      <h4>{{ $t('admin_page.notification_event_settings_title') }}</h4>
      <p>{{ $t('admin_page.notification_event_settings_description') }}</p>
    </div>

    <div v-if="isLoading" class="ne-loading-state">
      {{ $t('admin_page.notification_event_settings_loading') }}
    </div>

    <div v-else class="ne-list">
      <div
        v-for="eventSetting in eventSettings"
        :key="eventSetting.eventKey"
        class="ne-item"
        :class="{ 'ne-item-disabled': !isEventEnabled(eventSetting.eventKey) }"
        :aria-disabled="!isEventEnabled(eventSetting.eventKey)"
      >
        <div class="ne-item-content">
          <div class="ne-title-row">
            <b-checkbox
              :value="isEventEnabled(eventSetting.eventKey)"
              @input="onEnabledChanged(eventSetting, $event)"
            />
            <div class="ne-cell-title">{{ eventSetting.label }}</div>
          </div>
          <div v-if="eventSetting.description" class="ne-cell-subtitle">
            {{ eventSetting.description }}
          </div>
        </div>

        <div class="ne-item-actions">
          <b-field>
            <b-select
              :value="localAssignments[eventSetting.eventKey] || ''"
              expanded
              :loading="savingEventKey === eventSetting.eventKey"
              :disabled="!isEventEnabled(eventSetting.eventKey)"
              @input="onTemplateSelected(eventSetting.eventKey, $event)"
            >
              <option
                v-for="template in getTemplatesForEvent(eventSetting)"
                :key="template._id"
                :value="template._id"
              >
                {{ template.name }}
              </option>
            </b-select>
          </b-field>

          <p
            v-if="
              eventSetting.assignedTemplate &&
              eventSetting.assignedTemplate.isActive === false
            "
            class="ne-warning"
          >
            {{ $t('admin_page.notification_event_settings_archived_warning') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'notification-event-settings-table',
  props: {
    eventSettings: {
      type: Array,
      required: true,
    },
    templates: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    savingEventKey: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localAssignments: {},
      localEnabled: {},
    };
  },
  methods: {
    isEventEnabled(eventKey) {
      return this.localEnabled[eventKey] !== false;
    },
    getTemplatesForEvent(eventSetting) {
      const activeTemplates = (this.templates || []).filter(
        (template) => template.isActive
      );
      const assignedTemplate = eventSetting?.assignedTemplate;
      const fallbackAssignedTemplates = assignedTemplate
        ? [assignedTemplate]
        : [];

      return Array.from(
        new Map(
          [...activeTemplates, ...fallbackAssignedTemplates].map((template) => [
            template._id,
            template,
          ])
        ).values()
      );
    },
    onTemplateSelected(eventKey, templateId) {
      this.localAssignments = {
        ...this.localAssignments,
        [eventKey]: templateId,
      };

      this.$emit('save-assignment', {
        eventKey,
        templateId: templateId || null,
        enabled: this.localEnabled[eventKey] !== false,
      });
    },
    onEnabledChanged(eventSetting, enabled) {
      const { eventKey } = eventSetting;

      this.localEnabled = {
        ...this.localEnabled,
        [eventKey]: enabled,
      };

      this.$emit('save-assignment', {
        eventKey,
        templateId: this.localAssignments[eventKey] || null,
        enabled,
      });
    },
  },
  watch: {
    eventSettings: {
      immediate: true,
      deep: true,
      handler(value) {
        this.localAssignments = (value || []).reduce((acc, item) => {
          acc[item.eventKey] = item.assignedTemplateId || '';
          return acc;
        }, {});

        this.localEnabled = (value || []).reduce((acc, item) => {
          acc[item.eventKey] = item.enabled !== false;
          return acc;
        }, {});
      },
    },
  },
};
</script>

<style scoped>
.ne-table-wrap {
  padding: 12px;
  border: 1px solid #ececec;
  border-radius: 8px;
  margin-bottom: 20px;
  background: #fafafa;
}

.ne-header {
  margin-bottom: 12px;
}

.ne-header h4 {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
}

.ne-header p {
  margin: 0;
  color: #666;
}

.ne-loading-state {
  padding: 16px 0;
  color: #666;
}

.ne-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ne-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 16px;
  border: 1px solid #ececec;
  border-radius: 8px;
  background: #fff;
}

.ne-item-disabled {
  opacity: 0.6;
  background: #f7f7f7;
}

.ne-item-content {
  flex: 1 1 auto;
  min-width: 0;
}

.ne-item-actions {
  flex: 0 0 320px;
  max-width: 320px;
}

.ne-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ne-cell-title {
  font-weight: 700;
}

.ne-cell-subtitle {
  font-size: 0.9rem;
  color: #666;
  margin-top: 6px;
}

.ne-warning {
  margin-top: 6px;
  font-size: 0.85rem;
  color: #b86b00;
}

@media screen and (max-width: 900px) {
  .ne-item {
    flex-direction: column;
  }

  .ne-item-actions {
    width: 100%;
    max-width: none;
    flex-basis: auto;
  }
}
</style>
